/**
 * consentStorage — Thin localStorage wrapper for cookie consent persistence.
 *
 * Key: "ims-consent-v1"
 * Schema: { given: true, performance: boolean, marketing: boolean }
 *
 * Version suffix lets us safely invalidate old entries when the consent
 * categories change in future (bump to "ims-consent-v2", etc.).
 *
 * Deliberately has no dependency on React or Zustand — pure read/write utility
 * so it can be called from the store initialiser synchronously.
 */

const KEY = 'ims-consent-v1';

export interface PersistedConsent {
  given:       boolean;
  performance: boolean;
  marketing:   boolean;
}

/** Read persisted consent. Returns null when no valid entry exists. */
export function loadConsent(): PersistedConsent | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'given' in parsed &&
      typeof (parsed as Record<string, unknown>).given === 'boolean'
    ) {
      const p = parsed as Record<string, unknown>;
      return {
        given:       Boolean(p.given),
        performance: Boolean(p.performance),
        marketing:   Boolean(p.marketing),
      };
    }
  } catch {
    // Corrupt entry — treat as no consent stored
  }
  return null;
}

/** Persist the user's consent decision. */
export function saveConsent(consent: PersistedConsent): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(consent));
  } catch {
    // localStorage unavailable (private browsing quota exceeded etc.) — silent
  }
}

/** Clear persisted consent (e.g. when user re-opens the banner from footer). */
export function clearConsent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // silent
  }
}
